import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';

import { Subscription } from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { datadogRum } from '@datadog/browser-rum';

import { FaviconService, NavigationService } from '@tablesready/common-ui';
import { AppVariablesService } from '@tablesready/frontend-variables';
import { ThemeSwitchService } from '@tablesready/frontend-theme-switch';
import { AuthService } from '@tablesready/frontend-auth';
import { IonContent } from '@ionic/angular';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'tablesready-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent) ionContentRef?: IonContent;
  timezone = '';

  private readonly subscriptions = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly router: Router,
    private readonly theme: ThemeSwitchService,
    private readonly faviconService: FaviconService,
    private readonly angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private readonly routerEvents: NavigationService,
    private readonly appVariables: AppVariablesService,
    readonly authService: AuthService,
  ) {
    this.angulartics2GoogleTagManager.startTracking();

    let applicationId = '';
    let env = 'develop';
    let clientToken = '';

    try {
      applicationId = process.env.DD_RUM_APPID;
      env = process.env.DD_RUM_ENV;
      clientToken = process.env.DD_RUM_CLIENTTOKEN;
    } catch (e) {
      console.log({ e });
    }

    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.com',
      service: 'tablesready',
      version: '0.1.2',
      env,
      sessionSampleRate: 55,
      sessionReplaySampleRate: 25,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [
        'https://api.tablesready.com',
        'https://host.tablesready.com',
        'https://tablesready-production.herokuapp.com',
        'https://tablesready-staging.herokuapp.com',
      ],
    });

    datadogRum.startSessionReplayRecording();
    const rumContext = datadogRum.getInternalContext();

    if ((window as any).Beacon && rumContext) {
      (window as any).Beacon('identify', { sessionURL: rumContext.session_id });
    }
  }

  ngOnInit(): void {
    this.theme.initializeTheme();
    this.faviconService.generate();

    this.document.addEventListener('visibilitychange', this.visibilityChangeListener);

    const routerSub = this.router.events.subscribe((routerEvent) => {
      if (routerEvent instanceof NavigationEnd) {
        this.routerEvents.addEvent(routerEvent);
      }

      if (routerEvent instanceof NavigationError) {
        console.error('Navigation error: ', routerEvent);
      }
    });
    this.subscriptions.add(routerSub);

    const logoutSub = this.authService.onLogOut.subscribe(() => {
      void this.router.navigate(['/login']);
    });
    this.subscriptions.add(logoutSub);
  }

  reload(): void {
    location.reload();
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    this.subscriptions.unsubscribe();
  }

  private readonly visibilityChangeListener = (): void => {
    if (!this.document.hidden) {
      this.appVariables.getAppVariables().subscribe();
    }
  };
}
